import { makeObservable, observable, action, computed, runInAction } from 'mobx';
import { DotLottie } from '@dotlottie/dotlottie-js';
import type Animation from './Animation';
import type Asset from './Asset';

export default class AnimationExporter {
    parentAnimation: Animation;
    isLoading: boolean = false;
    exportReady: boolean = false;
    fileSize: string = '';
    error: string = '';
    dotLottieInstance: DotLottie | null = null;

    constructor(parent: Animation) {
        this.parentAnimation = parent;
        makeObservable(this, {
            isLoading: observable,
            fileSize: observable,
            error: observable,
            load: action,
            download: action,
            reset: action,
            isReady: computed,
            exportReady: observable,
        });
    }

    get isReady() {
        return this.dotLottieInstance !== null && this.error === '';
    }

    reset = () => {
        this.isLoading = false;
        this.fileSize = '';
        this.error = '';
        this.dotLottieInstance = null;
        this.exportReady = false;
    }

    load = async () => {
        const currentAsset: Asset | undefined = this.parentAnimation.currentAnimationAsset;
        if (!currentAsset) {
            runInAction(() => {
                this.error = 'No current animation asset available.';
                this.reset();
            });
            return;
        }

        this.isLoading = true;
        this.reset();

        try {
            const dotLottie = new DotLottie();

            await dotLottie
                .setAuthor('') // Customize as needed
                .setVersion('1.0')
                .addAnimation({
                    id: currentAsset.id,
                    url: currentAsset.downloadURL,
                    autoplay: true,
                    loop: false,
                })
                .build();

            // Calculate file size in kilobytes
            const blob = await dotLottie.toBlob();
            const fileSizeInBytes = blob.size;
            const fileSizeInKB = (fileSizeInBytes / 1024).toFixed(2);

            runInAction(() => {
                this.dotLottieInstance = dotLottie;
                this.fileSize = `${fileSizeInKB} KB`;
                this.exportReady = true; // Set exportReady to true on success
            });
        } catch (e) {
            runInAction(() => {
                this.error = 'Failed to create dotLottie export.';
                this.exportReady = false; // Ensure exportReady is false on failure
            });
            console.error(e);
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    download = async (filename: string = 'export.lottie') => {
        if (this.isLoading) {
            console.warn('AnimationExporter is still loading, cannot download.');
            return;
        }
        if (!this.dotLottieInstance) {
            console.warn('DotLottie instance is not ready, cannot download.');
            return;
        }

        try {
            await this.dotLottieInstance.download(filename);
        } catch (e) {
            runInAction(() => {
                this.error = 'Failed to download dotLottie file.';
            });
            console.error(e);
        }
    }
}